import type { Subscription } from 'centrifuge';
import { Centrifuge } from 'centrifuge';

const LOG_PREFIX = '[Centrifugo]';

export interface IConnectOptions {
  getToken?: () => Promise<string>;
  onConnected?: () => void;
  onDisconnected?: () => void;
}

/**
 * Single Centrifugo v5 client for the whole app. Consumers only subscribe once the
 * provider reports CONNECTED, so a subscription always has a live client behind it.
 */
class CentrifugoService {
  private client: Centrifuge | null = null;

  private readonly subscriptions = new Map<string, Subscription>();

  connect(url: string, token: string, options?: IConnectOptions): void {
    this.disconnect();

    const endpoint = `${url}/connection/websocket`;

    const client = new Centrifuge(endpoint, {
      token,
      ...(options?.getToken && { getToken: options.getToken }),
    });

    client.on('connecting', (ctx) => {
      console.info(`${LOG_PREFIX} connecting to ${endpoint}`, {
        code: ctx.code,
        reason: ctx.reason,
      });
    });

    client.on('connected', (ctx) => {
      console.info(`${LOG_PREFIX} connected`, {
        client: ctx.client,
        transport: ctx.transport,
      });
      options?.onConnected?.();
    });

    // Only fires on a terminal disconnect. Transient drops come through `connecting`,
    // and centrifuge restores the subscriptions itself — nothing to tear down there.
    client.on('disconnected', (ctx) => {
      console.warn(`${LOG_PREFIX} disconnected`, {
        code: ctx.code,
        reason: ctx.reason,
      });
      options?.onDisconnected?.();
    });

    client.on('error', (ctx) => {
      console.error(`${LOG_PREFIX} client error on ${endpoint}`, ctx.error);
    });

    this.client = client;

    client.connect();
  }

  disconnect(): void {
    const client = this.client;
    if (!client) return;

    this.subscriptions.forEach((sub) => {
      sub.unsubscribe();
      sub.removeAllListeners();
      client.removeSubscription(sub);
    });
    this.subscriptions.clear();

    client.removeAllListeners();
    client.disconnect();
    this.client = null;
  }

  // One subscriber per channel: centrifuge v5 throws on a second newSubscription() for the same channel
  subscribe<T>(channel: string, onMessage: (data: T) => void): void {
    if (!this.client || this.subscriptions.has(channel)) return;

    const sub = this.client.newSubscription(channel);

    // A throwing handler would propagate into centrifuge and abort the rest of the frame
    sub.on('publication', (ctx) => {
      try {
        onMessage(ctx.data as T);
      } catch (error) {
        console.error(`${LOG_PREFIX} handler error on ${channel}`, error);
      }
    });

    sub.on('error', (ctx) => {
      console.error(
        `${LOG_PREFIX} subscription error on ${channel}`,
        ctx.error
      );
    });

    sub.subscribe();
    this.subscriptions.set(channel, sub);
  }

  unsubscribe(channel: string): void {
    const sub = this.subscriptions.get(channel);
    if (!sub) return;

    sub.unsubscribe();
    sub.removeAllListeners();
    this.client?.removeSubscription(sub);
    this.subscriptions.delete(channel);
  }
}

export const centrifugoService = new CentrifugoService();
